import { Tuple }	from "ts-base/tuple";
import { Sync }		from "ts-base/sync";

//-----------------------------------------------------------------------------

export type Fn0<T>						= ()										=> T;
export type Fn1<S, T>					= (it:S)									=> T;
export type Fn2<S1, S2, T>				= (it1:S1, it2:S2)							=> T;
export type Fn3<S1, S2, S3, T>			= (it1:S1, it2:S2, it3:S3)					=> T;
export type Fn4<S1, S2, S3, S4, T>		= (it1:S1, it2:S2, it3:S3, it4:S4)			=> T;
export type Fn5<S1, S2, S3, S4, S5, T>	= (it1:S1, it2:S2, it3:S3, it4:S4, it5:S5)	=> T;

//-----------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyFn		= Fn<any, any>;

export type Fn<S, T>	= Fn1<S, T>;

export namespace Fn {
	// parameter of an Fn
	export type Parameter<T extends AnyFn>	= T extends Fn<infer X,	unknown> ? X : never;

	// result of an Fn
	export type Result<T extends AnyFn>		= T extends Fn<never,	infer X> ? X : never;

	//-----------------------------------------------------------------------------

	/** useful for exhaustiveness checks: pass the discriminator deemed impossible to make the compiler prove it can not occur */
	export const impossible	= (it:never):never	=> it;

	/** useful while implementing functions as never is a subtype of everything so you can always return the value of this */
	export const never = (message:string = "up"):never => { throw new Error(message); };

	/** returns its input unmodified */
	export const identity = <T>(it:T):T =>
		it;

	/** always return value, regardless of input */
	export const constant = <S, T>(value:T) => (_ignored:S):T =>
		value;

	export const constantC = <T>(value:T) => <S>(_ignored:S):T =>
		value;

	export const apply	= <S, T>(value:S, func:Fn<S, T>):T		=> func(value);
	export const applyC	= <S>(value:S) => <T>(func:Fn<S, T>):T	=> func(value);

	/** flip first two arguments of a curried function */
	export const flip = <S1, S2, T>(fn:Fn<S1, Fn<S2, T>>):Fn<S2, Fn<S1, T>> =>
		(s2) => (s1) => fn(s1)(s2);

	export const pure	= constant;

	// export const map		= andThen;

	// forms a monad with constant
	export const flatMap	= <S, T>(self:Fn<S, T>) => <U>(func:Fn<T, Fn<S, U>>):Fn<S, U> =>
		(s) => func(self(s))(s);

	export const delay	= <S, T>(self:Fn0<Fn<S, T>>):Fn<S, T>	=>
		(s) => self()(s);

	//-----------------------------------------------------------------------------

	/** compose multiple functions doing first a and then b */
	export const andThen = <T1, T2, T3> (a:Fn<T1, T2>, b:Fn<T2, T3>):Fn<T1, T3> => (it:T1):T3 =>
		b(a(it));

	export const andThen3 = <T1, T2, T3, T4>(a:Fn<T1, T2>, b:Fn<T2, T3>, c:Fn<T3, T4>):Fn<T1, T4> =>
		andThen(andThen(a, b), c);

	export const andThen4 = <T1, T2, T3, T4, T5>(a:Fn<T1, T2>, b:Fn<T2, T3>, c:Fn<T3, T4>, d:Fn<T4, T5>):Fn<T1, T5> =>
		andThen(andThen(andThen(a, b), c), d);

	export const andThen5 = <T1, T2, T3, T4, T5, T6>(a:Fn<T1, T2>, b:Fn<T2, T3>, c:Fn<T3, T4>, d:Fn<T4, T5>, e:Fn<T5, T6>):Fn<T1, T6> =>
		andThen(andThen(andThen(andThen(a, b), c), d), e);

	export const andThen6 = <T1, T2, T3, T4, T5, T6, T7>(a:Fn<T1, T2>, b:Fn<T2, T3>, c:Fn<T3, T4>, d:Fn<T4, T5>, e:Fn<T5, T6>, f:Fn<T6, T7>):Fn<T1, T7> =>
		andThen(andThen(andThen(andThen(andThen(a, b), c), d), e), f);

	export type AndThenN<P extends ReadonlyArray<AnyFn>>	=
		P extends [ infer A extends AnyFn ]									?	A					:
		P extends [ infer A extends AnyFn, ...(infer B extends Array<AnyFn>) ]	?	AndThen<A, AndThenN<B>>	:
		never;

	export type AndThen<A extends AnyFn, B extends AnyFn>	=
		Fn.Result<A> extends Fn.Parameter<B>
		?	Fn<Fn.Parameter<A>, Fn.Result<B>>
		:	never;

	export const andThenN	= <P extends ReadonlyArray<AnyFn>>(...functions:P):AndThenN<P>	=>
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		functions.reduce((a, b) => (x) => b(a(x))) as any;

	export const map		= andThen;

	//-----------------------------------------------------------------------------

	/** compose multiple functions like the mathematicians do */
	export const compose = <T1, T2, T3> (a:Fn<T2, T3>, b:Fn<T1, T2>):Fn<T1, T3> =>
		andThen(b, a);

	export const compose3 = <T1, T2, T3, T4>(a:Fn<T3, T4>, b:Fn<T2, T3>, c:Fn<T1, T2>):Fn<T1, T4> =>
		andThen3(c, b, a);

	export const compose4 = <T1, T2, T3, T4, T5>(a:Fn<T4, T5>, b:Fn<T3, T4>, c:Fn<T2, T3>, d:Fn<T1, T2>):Fn<T1, T5> =>
		andThen4(d, c, b, a);

	export const compose5 = <T1, T2, T3, T4, T5, T6>(a:Fn<T5, T6>, b:Fn<T4, T5>, c:Fn<T3, T4>, d:Fn<T2, T3>, e:Fn<T1, T2>):Fn<T1, T6> =>
		andThen5(e, d, c, b, a);

	export const compose6 = <T1, T2, T3, T4, T5, T6, T7>(a:Fn<T6, T7>, b:Fn<T5, T6>, c:Fn<T4, T5>, d:Fn<T3, T4>, e:Fn<T2, T3>, f:Fn<T1, T2>):Fn<T1, T7> =>
		andThen6(f, e, d, c, b, a);

	export type ComposeN<P extends ReadonlyArray<AnyFn>>	= AndThenN<Tuple.Reverse<P>>;

	export type Compose<A extends AnyFn, B extends AnyFn>	= AndThen<B, A>;

	export const composeN	= <P extends ReadonlyArray<AnyFn>>(...functions:P):ComposeN<P>	=>
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		functions.reduce((a, b) => (x) => a(b(x))) as any;

	export const contraMap	= compose;

	//-----------------------------------------------------------------------------
	//## applicative functor

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	export type AnySelfRecord	= Record<string, Fn<any, any>>;

	export type ParameterRecord<F extends AnySelfRecord>	= {
		[ K in keyof F ]: Parameters<F[K]>[0]
	};

	export type ResultRecord<F extends AnySelfRecord>	= {
		[ K in keyof F ]: ReturnType<F[K]>
	};

	export const applied	= <const F extends AnySelfRecord>(functions:F) => (params:ParameterRecord<F>):Readonly<ResultRecord<F>>	=> {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const out	= {} as any;
		for (const [ k, f ] of Object.entries(functions)) {
			out[k]	= f(params[k]);
		}
		return out;
	};

	//-----------------------------------------------------------------------------

	/** execute a side effect and return the original input */
	export const tap	= <T>(action:(value:T)=>void) => (value:T):T => {
		action(value);
		return value;
	};

	export const memoize	= <S, T>(self:Fn<S, T>):Sync<Fn<S, T>>	=> () => {
		const cache	= new Map<S, T>();

		return (input:S):T	=> {
			if (cache.has(input)) {
				return cache.get(input) as T;
			}
			else {
				const output	= self(input);
				cache.set(input, output);
				return output;
			}
		};
	};
}
